import { Box, Typography } from "@mui/material";
import axios from "axios";
import React, { useState, useRef, useEffect } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { API_URL } from "./Constant";
import { HTML5Backend } from "react-dnd-html5-backend";

const CroswordCreatorGrid = ({
  question,
  setGroupedQuestions,
  setIsAnswersPlace,
}) => {
  const [grid, setGrid] = useState(
    Array(8)
      .fill("")
      .map(() => Array(8).fill("")),
  );
  const [highlightedCell, setHighlightedCell] = useState(null);
  const [highlightedCells, setHighlightedCells] = useState([]); // Track highlighted cells
  const [answers, setAnswers] = useState([]); // Track answers in the grid
  const [answersPlaceError, setAnswersPlaceError] = useState("");
  const gridRef = useRef(null);
  const [allQuestion, setAllQuestion] = useState([]);

  const getAnswerBounds = (answerCells) => {
    const rows = answerCells.map((cell) => cell.row);
    const cols = answerCells.map((cell) => cell.col);

    return {
      minRow: Math.min(...rows),
      maxRow: Math.max(...rows),
      minCol: Math.min(...cols),
      maxCol: Math.max(...cols),
    };
  };

  const [, dragAns] = useDrag(() => ({
    type: "Answer",
    item: { question },
  }));

  const fetchAllQuestions = async () => {
    try {
      const response = await axios.post(`${API_URL}/questions_temp`, {
        CreatorTocken: localStorage.getItem("CreatorTocken"),
        game_id: localStorage.getItem("gameId"),
      });

      console.log("Fetched Question Data:", response.data);
      setAllQuestion(response.data); // Store original questions

      // Initialize a clean 8x8 grid
      const newGrid = Array.from({ length: 8 }, () => Array(8).fill(""));
      const newAnswers = [];

      response.data.forEach((question) => {
        const { answer, direction, row_index, col_index } = question;

        if (
          row_index === null ||
          col_index === null ||
          typeof row_index !== "number" ||
          typeof col_index !== "number"
        ) {
          console.warn(
            "Skipping question due to missing coordinates:",
            question,
          );
          return;
        }

        const answerParts = answer.split(",");
        const cells = [];

        // Preload answers from the database
        if (direction === "across") {
          answerParts.forEach((part, index) => {
            const col = col_index + index;
            if (row_index >= 0 && row_index < 8 && col >= 0 && col < 8) {
              newGrid[row_index][col] = part;
              cells.push({ row: row_index, col });
            }
          });
        } else if (direction === "down") {
          answerParts.forEach((part, index) => {
            const row = row_index + index;
            if (row >= 0 && row < 8 && col_index >= 0 && col_index < 8) {
              newGrid[row][col_index] = part;
              cells.push({ row, col: col_index });
            }
          });
        }

        // Add preloaded answer to the list
        newAnswers.push({
          answer,
          direction,
          isPlaced: true, // Mark as preloaded
          cells,
        });
      });

      console.log("Grid after preloading:", newGrid);
      setGrid(newGrid);
      setAnswers(newAnswers);
      setIsAnswersPlace("true");
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    // Function to fetch questions
    const fetchQuestions = async () => {
      await fetchAllQuestions();
    };

    // Call the fetch function initially
    fetchQuestions();
    if (answersPlaceError !== "") {
      setAnswersPlaceError("");
    }

    // Set up interval to call fetch function every minute
    const interval = setInterval(() => {
      fetchQuestions();
    }, 100); // 60000 ms = 1 minute

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, []);

  const highlightAnswer = (answerCells) => {
    setHighlightedCells(answerCells);
  };

  const isCellHighlighted = (row, col) => {
    return highlightedCells.some(
      (cell) => cell.row === row && cell.col === col,
    );
  };

  const isTopEdge = (rowIndex, colIndex, associatedAnswer) => {
    return !associatedAnswer.cells.some(
      (cell) => cell.row === rowIndex - 1 && cell.col === colIndex,
    );
  };

  const isBottomEdge = (rowIndex, colIndex, associatedAnswer) => {
    return !associatedAnswer.cells.some(
      (cell) => cell.row === rowIndex + 1 && cell.col === colIndex,
    );
  };

  const isLeftEdge = (rowIndex, colIndex, associatedAnswer) => {
    return !associatedAnswer.cells.some(
      (cell) => cell.row === rowIndex && cell.col === colIndex - 1,
    );
  };

  const isRightEdge = (rowIndex, colIndex, associatedAnswer) => {
    return !associatedAnswer.cells.some(
      (cell) => cell.row === rowIndex && cell.col === colIndex + 1,
    );
  };

  const [{ isOver }, drop] = useDrop(() => ({
    accept: "question",
    hover: (item, monitor) => {
      const hoverPosition = monitor.getClientOffset();
      if (hoverPosition && gridRef.current) {
        const gridRect = gridRef.current.getBoundingClientRect();
        const cellWidth = gridRect.width / 8;
        const cellHeight = gridRect.height / 8;

        const row = Math.floor((hoverPosition.y - gridRect.top) / cellHeight);
        const col = Math.floor((hoverPosition.x - gridRect.left) / cellWidth);

        if (row >= 0 && row < 8 && col >= 0 && col < 8) {
          setHighlightedCell({ row, col });
        }
      }
    },
    drop: (item, monitor) => {
      const dropPosition = monitor.getClientOffset();
      if (dropPosition && gridRef.current) {
        const gridRect = gridRef.current.getBoundingClientRect();
        const cellWidth = gridRect.width / 8;
        const cellHeight = gridRect.height / 8;

        const startRow = Math.floor(
          (dropPosition.y - gridRect.top) / cellHeight,
        );
        const startCol = Math.floor(
          (dropPosition.x - gridRect.left) / cellWidth,
        );

        placeAnswerInGrid(item.question, startRow, startCol);
        setHighlightedCell(null);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  }));

  const [{ isOverAnswer }, AnswerDrop] = useDrop(() => ({
    accept: "Answer",
    hover: (item, monitor) => {
      const hoverPosition = monitor.getClientOffset();
      if (hoverPosition && gridRef.current) {
        const gridRect = gridRef.current.getBoundingClientRect();
        const cellWidth = gridRect.width / 8;
        const cellHeight = gridRect.height / 8;

        const row = Math.floor((hoverPosition.y - gridRect.top) / cellHeight);
        const col = Math.floor((hoverPosition.x - gridRect.left) / cellWidth);

        if (row >= 0 && row < 8 && col >= 0 && col < 8) {
          handleDropAnswer(item, row, col);
        }
      }
    },
  }));

  const isValidPlacement = (selectedQuestion, row, col, letter) => {
    const existingLetter = grid[row][col];

    if (existingLetter && existingLetter !== letter) {
      // Check if the cell is part of an answer and the letter matches
      const isSharedOverlap = answers.some((ans) =>
        ans.cells.some(
          (cell) =>
            cell.row === row &&
            cell.col === col &&
            grid[cell.row][cell.col] === letter,
        ),
      );

      if (!isSharedOverlap) return false; // Invalid overlap
    }

    return true; // Valid placement
  };

  const placeAnswerInGrid = async (selectedQuestion, startRow, startCol) => {
    const { answer, direction } = selectedQuestion;
    const answerParts = answer.split(",");
    const gridSize = 8; // Assuming an 8x8 grid
    setAnswersPlaceError("");

    // Validate placement within boundaries
    if (direction === "across") {
      if (startCol + answerParts.length > gridSize) {
        setAnswersPlaceError(
          "Answer cannot be placed here, it exceeds grid boundaries.",
        );
        return;
      }
    } else if (direction === "down") {
      if (startRow + answerParts.length > gridSize) {
        setAnswersPlaceError(
          "Answer cannot be placed here, it exceeds grid boundaries.",
        );
        return;
      }
    }

    const newGrid = [...grid];
    const newAnswers = [...answers];

    // Place answer in the grid with validation
    if (direction === "across") {
      for (let index = 0; index < answerParts.length; index++) {
        const colIndex = startCol + index;

        if (
          colIndex < gridSize &&
          isValidPlacement(
            selectedQuestion,
            startRow,
            colIndex,
            answerParts[index],
          )
        ) {
          newGrid[startRow][colIndex] = answerParts[index];
        } else {
          setAnswersPlaceError(
            "Sorry, the answer cannot be placed in the selected cell.",
          );
          return; // Exit early if placement is invalid
        }
      }

      newAnswers.push({
        answer,
        cells: answerParts.map((_, index) => ({
          row: startRow,
          col: startCol + index,
        })),
      });
    } else if (direction === "down") {
      for (let index = 0; index < answerParts.length; index++) {
        const rowIndex = startRow + index;

        if (
          rowIndex < gridSize &&
          isValidPlacement(
            selectedQuestion,
            rowIndex,
            startCol,
            answerParts[index],
          )
        ) {
          newGrid[rowIndex][startCol] = answerParts[index];
        } else {
          setAnswersPlaceError(
            "Sorry, the answer cannot be placed in the selected cell.",
          );
          return; // Exit early if placement is invalid
        }
      }

      newAnswers.push({
        answer,
        cells: answerParts.map((_, index) => ({
          row: startRow + index,
          col: startCol,
        })),
      });
    }

    // Update grid and answers state
    setGrid(newGrid);
    setAnswers(newAnswers);

    // Remove placed question from groupedQuestions
    // setGroupedQuestions(prevGroupedQuestions => {
    //   // Ensure prevGroupedQuestions is an array
    //   if (!Array.isArray(prevGroupedQuestions)) {
    //     console.error("groupedQuestions is not an array:", prevGroupedQuestions);
    //     return []; // Reset to an empty array if it's not an array
    //   }
    //   return prevGroupedQuestions.filter(
    //     q => q.question_number !== selectedQuestion.question_number
    //   );
    // });

    // Update the database with row_index and col_index
    try {
      const response = await axios.post(`${API_URL}/update_question_index`, {
        CreatorTocken: localStorage.getItem("CreatorTocken"),
        question_id: selectedQuestion.question_number, // Ensure question_number exists
        row_index: startRow,
        col_index: startCol,
      });
      console.log("Question coordinates updated successfully:", response.data);
    } catch (error) {
      console.error(
        "Error updating question coordinates:",
        error.response?.data || error.message,
      );
    }

    fetchAllQuestions(); // Fetch updated questions
  };

  const deleteAnswer = async (answerToDelete) => {
    try {
      console.log("Flattened answers to search:", allQuestion);

      // Find the matching answer object
      const answerFound = allQuestion.find((a) => a.answer === answerToDelete);
      console.log("Answer found:", answerFound);

      const newGrid = grid.map((row) => [...row]); // Create a deep copy of the current grid
      const answerToRemove = answers.find((a) => a.answer === answerToDelete);

      if (answerToRemove) {
        // Clear the associated cells in the new grid
        answerToRemove.cells.forEach((cell) => {
          if (
            cell.row >= 0 &&
            cell.row < newGrid.length &&
            cell.col >= 0 &&
            cell.col < newGrid[0].length
          ) {
            newGrid[cell.row][cell.col] = "";
          }
        });
      }

      // Remove the answer from answers array
      const newAnswers = answers.filter((a) => a.answer !== answerToDelete);

      // Update the local state
      setGrid(newGrid);
      setAnswers(newAnswers);
      // Send delete request to server
      const response = await axios.post(`${API_URL}/delete_question_index`, {
        CreatorTocken: localStorage.getItem("CreatorTocken"),
        question_id: answerFound?.question_number,
      });

      console.log("Server response:", response.data);
      if (response.data.success === true) {
        // Optionally refetch questions if needed
        fetchAllQuestions();
        // window.location.reload();
      }
    } catch (error) {
      console.error(
        "Error during answer deletion:",
        error.response?.data || error.message,
      );
    }
  };

  const handleDropAnswer = (item, row, col) => {
    if (!item) return;

    const answerToMove = answers.find((ans) => ans.answer === item.question);

    if (!answerToMove) return;

    const newGrid = grid.map((row) => [...row]);
    const newAnswers = answers.filter((ans) => ans.answer !== item.question);

    // Clear previous positions
    answerToMove.cells.forEach((cell) => {
      if (cell.row >= 0 && cell.row < 8 && cell.col >= 0 && cell.col < 8) {
        newGrid[cell.row][cell.col] = "";
      }
    });

    // Assign new cells
    const newCells = answerToMove.cells.map((_, index) => ({
      row: row + index,
      col,
      letter: answerToMove.answer[index], // Map the actual letter at the respective index
    }));

    newCells.forEach((cell) => {
      if (cell.row >= 0 && cell.row < 8 && cell.col >= 0 && cell.col < 8) {
        newGrid[cell.row][cell.col] = cell.letter; // Use the letter mapped earlier
      }
    });

    // Update state
    setGrid(newGrid);
    setAnswers([...newAnswers, { answer: item.question, cells: newCells }]);
  };

  const renderBoxNumbers = (rowIndex, colIndex) => {
    const boxNumber = rowIndex * 8 + colIndex + 1;
    return (
      <span
        style={{
          fontSize: "10px",
          fontWeight: "normal",
          position: "absolute",
          top: "5px",
          left: "5px",
        }}
      >
        {boxNumber}
      </span>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        justifyContent: "flex-start",
        alignItems: "center",
      }}
    >
      <Box
        ref={(node) => {
          drop(node);
          gridRef.current = node;
        }}
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(8, 1fr)",
          gridGap: "4px",
          width: "100%",
          height: "100%",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        {grid.map((row, rowIndex) =>
          row.map((cell, colIndex) => {
            const isHighlighted =
              highlightedCell &&
              highlightedCell.row === rowIndex &&
              highlightedCell.col === colIndex;

            const associatedAnswer = answers.find((a) =>
              a.cells.some((c) => c.row === rowIndex && c.col === colIndex),
            );
            const isLastCellOfAnswer = associatedAnswer
              ? associatedAnswer.cells[associatedAnswer.cells.length - 1]
                  .row === rowIndex &&
                associatedAnswer.cells[associatedAnswer.cells.length - 1]
                  .col === colIndex
              : false;

            return (
              <div
                key={`${rowIndex}-${colIndex}`}
                ref={AnswerDrop}
                style={{
                  width: "100%",
                  height: "44px",
                  border: isHighlighted ? "2px dashed blue" : "1px solid black",
                  borderTop:
                    associatedAnswer &&
                    isTopEdge(rowIndex, colIndex, associatedAnswer)
                      ? "2px solid red"
                      : "1px solid black",
                  borderBottom:
                    associatedAnswer &&
                    isBottomEdge(rowIndex, colIndex, associatedAnswer)
                      ? "2px solid red"
                      : "1px solid black",
                  borderLeft:
                    associatedAnswer &&
                    isLeftEdge(rowIndex, colIndex, associatedAnswer)
                      ? "2px solid red"
                      : "1px solid black",
                  borderRight:
                    associatedAnswer &&
                    isRightEdge(rowIndex, colIndex, associatedAnswer)
                      ? "2px solid red"
                      : "1px solid black",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#f1f3fc",
                  position: "relative",
                }}
              >
                <DndProvider backend={HTML5Backend}>
                  {renderBoxNumbers(rowIndex, colIndex)}

                  <input
                    ref={dragAns}
                    type="text"
                    maxLength="1"
                    value={cell}
                    onChange={(e) => {
                      const newValue = e.target.value.toUpperCase();
                      if (newValue.match(/^[A-Z]$/) || newValue === "") {
                        // Allow only alphabetic input
                        const newGrid = [...grid];
                        newGrid[rowIndex][colIndex] = newValue;
                        setGrid(newGrid);
                      }
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      textAlign: "center",
                      fontSize: "14px",
                      backgroundColor: "transparent",
                      border: "none",
                      outline: "none",
                      color: "#283655",
                      cursor: "pointer",
                    }}
                  />
                </DndProvider>

                {isLastCellOfAnswer && (
                  <button
                    onClick={() => deleteAnswer(associatedAnswer.answer)}
                    style={{
                      position: "absolute",
                      borderRadius: "28px",
                      right: "2px",
                      top: "2px", // Adjusted to top-right corner
                      fontSize: "10px",
                      cursor: "pointer",
                      background: "red", // Optional: Transparent background
                      border: "none", // Optional: No border
                      color: "white", // Optional: Red color for visibility
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            );
          }),
        )}
      </Box>
      {answersPlaceError && (
        <Typography
          sx={{
            color: "red",
            fontFamily: "Roboto",
            fontSize: "14px",
            width: "100%",
            whiteSpace: "nowrap",
          }}
        >
          {answersPlaceError}
        </Typography>
      )}
    </Box>
  );
};

export default CroswordCreatorGrid;
